import { useState, type FormEvent } from "react";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Caja, type Transferencia } from "@/lib/api";
import {
  aPayloadTransferencia,
  formTransferencia,
  mensajeBajaCaja,
  validarNombreCaja,
  validarTransferencia,
  type ErroresTransferencia,
  type FormTransferencia,
  type PayloadTransferencia,
} from "@/lib/cajas";

type DialogoPanel =
  | { tipo: "caja"; caja: Caja | null }
  | { tipo: "borrarCaja"; caja: Caja }
  | { tipo: "transferencia"; transferencia: Transferencia | null }
  | { tipo: "borrarTransferencia"; transferencia: Transferencia }
  | null;

const SIN_ERRORES: ErroresTransferencia = {};

/**
 * Lógica (sin JSX) de las cajas dentro de Caja (Mati, 2026-10-19): alta y renombrar, cuál es la principal, archivar,
 * borrar (o archivar si tiene movimientos) y las transferencias entre cajas. Sólo el admin escribe. `recargar` refresca
 * la lista de cajas, los saldos y el libro.
 */
export function usePanelCajas(fid: number, cajas: Caja[], recargar: () => void) {
  const base = `/api/fideicomisos/${fid}`;
  const [dialogo, setDialogo] = useState<DialogoPanel>(null);
  const [nombre, setNombre] = useState("");
  const [form, setForm] = useState<FormTransferencia>(() => formTransferencia(undefined, cajas));
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const accionCaja = useAsyncAction((id: number | null, datos: Partial<Pick<Caja, "nombre" | "activo" | "principal">>) =>
    id === null ? api.post<Caja>(`${base}/cajas`, datos) : api.patch<Caja>(`${base}/cajas/${id}`, datos),
  );
  const accionBorrarCaja = useAsyncAction((id: number) => api.delete<{ resultado: "borrado" | "archivado" }>(`${base}/cajas/${id}`));
  const accionTransferencia = useAsyncAction((id: number | null, datos: PayloadTransferencia) =>
    id === null ? api.post<Transferencia>(`${base}/caja/transferencias`, datos) : api.patch<Transferencia>(`${base}/caja/transferencias/${id}`, datos),
  );
  const accionCargar = useAsyncAction((id: number) => api.get<Transferencia>(`${base}/caja/transferencias/${id}`));
  const accionBorrarTransferencia = useAsyncAction((id: number) => api.delete<void>(`${base}/caja/transferencias/${id}`).then(() => true));

  const cerrar = () => setDialogo(null);
  const errorNombre = validarNombreCaja(nombre);
  const errores = validarTransferencia(form);

  const abrirCaja = (caja: Caja | null) => {
    setNombre(caja?.nombre ?? "");
    setMostrarErrores(false);
    accionCaja.limpiarError();
    setDialogo({ tipo: "caja", caja });
  };

  const guardarCaja = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "caja" || errorNombre) return;
    if (await accionCaja.run(dialogo.caja?.id ?? null, { nombre: nombre.trim() })) {
      cerrar();
      recargar();
    }
  };

  /** Hacer principal, archivar y reactivar: un PATCH directo, sin diálogo. */
  const cambiarCaja = async (caja: Caja, datos: Partial<Pick<Caja, "activo" | "principal">>) => {
    setAviso(null);
    accionCaja.limpiarError();
    if (await accionCaja.run(caja.id, datos)) recargar();
  };

  const abrirBorrarCaja = (caja: Caja) => {
    accionBorrarCaja.limpiarError();
    setDialogo({ tipo: "borrarCaja", caja });
  };

  const confirmarBorrarCaja = async () => {
    if (dialogo?.tipo !== "borrarCaja") return;
    const r = await accionBorrarCaja.run(dialogo.caja.id);
    if (r) {
      setAviso(mensajeBajaCaja(dialogo.caja.nombre, r.resultado));
      cerrar();
      recargar();
    }
  };

  const abrirTransferencia = () => {
    setForm(formTransferencia(undefined, cajas));
    setMostrarErrores(false);
    accionTransferencia.limpiarError();
    setDialogo({ tipo: "transferencia", transferencia: null });
  };

  /** Desde una línea del libro: trae la transferencia completa (el libro no tiene la nota ni las dos cajas por id). */
  const abrirEditarTransferencia = async (id: number) => {
    accionCargar.limpiarError();
    const tr = await accionCargar.run(id);
    if (!tr) return;
    setForm(formTransferencia(tr, cajas));
    setMostrarErrores(false);
    accionTransferencia.limpiarError();
    setDialogo({ tipo: "transferencia", transferencia: tr });
  };

  const abrirBorrarTransferencia = async (id: number) => {
    accionCargar.limpiarError();
    const tr = await accionCargar.run(id);
    if (!tr) return;
    accionBorrarTransferencia.limpiarError();
    setDialogo({ tipo: "borrarTransferencia", transferencia: tr });
  };

  const guardarTransferencia = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "transferencia" || Object.keys(errores).length > 0) return;
    if (await accionTransferencia.run(dialogo.transferencia?.id ?? null, aPayloadTransferencia(form))) {
      cerrar();
      recargar();
    }
  };

  const confirmarBorrarTransferencia = async () => {
    if (dialogo?.tipo !== "borrarTransferencia") return;
    if (await accionBorrarTransferencia.run(dialogo.transferencia.id)) {
      cerrar();
      recargar();
    }
  };

  return {
    dialogo,
    cerrar,
    aviso,
    errorAccion: accionCaja.error && dialogo?.tipo !== "caja" ? accionCaja.error : null,
    // cajas
    abrirCaja,
    nombre,
    setNombre,
    errorNombre: mostrarErrores ? errorNombre : null,
    guardarCaja,
    guardandoCaja: accionCaja.loading,
    errorGuardarCaja: dialogo?.tipo === "caja" ? accionCaja.error : null,
    hacerPrincipal: (caja: Caja) => void cambiarCaja(caja, { principal: true }),
    archivar: (caja: Caja) => void cambiarCaja(caja, { activo: !caja.activo }),
    abrirBorrarCaja,
    confirmarBorrarCaja,
    borrandoCaja: accionBorrarCaja.loading,
    errorBorrarCaja: accionBorrarCaja.error,
    // transferencias
    abrirTransferencia,
    abrirEditarTransferencia,
    abrirBorrarTransferencia,
    cargandoTransferencia: accionCargar.loading,
    errorCargarTransferencia: accionCargar.error,
    form,
    cambiar: <K extends keyof FormTransferencia>(campo: K, valor: FormTransferencia[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    guardarTransferencia,
    guardandoTransferencia: accionTransferencia.loading,
    errorGuardarTransferencia: accionTransferencia.error,
    confirmarBorrarTransferencia,
    borrandoTransferencia: accionBorrarTransferencia.loading,
    errorBorrarTransferencia: accionBorrarTransferencia.error,
  };
}

export type PanelCajas = ReturnType<typeof usePanelCajas>;
